import React from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  Flex,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {usePdpStockIndicator} from '@amblique/king-living/app/hooks/use-pdp-stock-indicator';
import {STOCK_INDICATOR} from '@amblique/king-living/app/utils/pdp-stock-utils';

/**
 * ProductStockIndicator — availability row on PDP with 2 states:
 * - IN_STOCK: green dot, "In stock"
 * - OUT_OF_STOCK: red dot, "Out of stock" + Notify Me button
 *
 * Row variant: {@link getPdpStockIndicator}.
 *
 * @param {Object} product - Product from SCAPI
 * @param {Object} variant - Selected variant
 * @param {Function} onNotifyMeClick - Called when Notify Me button is clicked
 * @param {string|undefined} [shopperInventoryIds] - IM-004 SCAPI `inventoryIds` string from cookie
 */
const ProductStockIndicator = ({
  product,
  variant,
  onNotifyMeClick,
  shopperInventoryIds,
  indicatorVariant = 'default',
}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('ProductStockIndicator', {variant: indicatorVariant});
  const isDesktop = useBreakpointValue({base: false, lg: true});
  const useDesktopCopy = indicatorVariant === 'strip' || isDesktop;

  const inStockLabel = intl.formatMessage({
    defaultMessage: 'In stock. Fastest available delivery',
    id: 'product_stock_indicator.in_stock',
  });
  const inStockMobileLabel = intl.formatMessage({
    defaultMessage: 'In stock. Fastest delivery',
    id: 'product_stock_indicator.in_stock_mobile',
  });
  const lowStockLabel = intl.formatMessage({
    defaultMessage: 'Low stock. Fastest available delivery',
    id: 'product_stock_indicator.low_stock',
  });
  const lowStockMobileLabel = intl.formatMessage({
    defaultMessage: 'Low stock. Fastest delivery',
    id: 'product_stock_indicator.low_stock_mobile',
  });
  const outOfStockLabel = intl.formatMessage({
    defaultMessage: 'Currently unavailable. Get notified',
    id: 'product_stock_indicator.out_of_stock',
  });

  const {row} = usePdpStockIndicator({product, shopperInventoryIds, variant});

  if (row === STOCK_INDICATOR.IN_STOCK) {
    return (
      <Flex className="product-stock-indicator__root" {...styles.inStockFlex}>
        <Box className="product-stock-indicator__dot-wrap" {...styles.dotWrap}>
          <Box className="product-stock-indicator__dot" {...styles.dotInStock} />
        </Box>
        <Text {...styles.inStockText}>
          {useDesktopCopy ? inStockLabel : inStockMobileLabel}
        </Text>
      </Flex>
    );
  }

  if (row === STOCK_INDICATOR.LOW_STOCK) {
    return (
      <Flex className="product-stock-indicator__root" {...styles.lowStockFlex}>
        <Box className="product-stock-indicator__dot-wrap" {...styles.dotWrap}>
          <Box className="product-stock-indicator__dot" {...styles.dotLowStock} />
        </Box>
        <Text {...styles.lowStockText}>
          {useDesktopCopy ? lowStockLabel : lowStockMobileLabel}
        </Text>
      </Flex>
    );
  }

  if (row === STOCK_INDICATOR.OUT_OF_STOCK) {
    return (
      <Flex className="product-stock-indicator__root" {...styles.outOfStockFlex}>
        <Box className="product-stock-indicator__dot-wrap" {...styles.dotWrap}>
          <Box className="product-stock-indicator__dot" {...styles.dotOutOfStock} />
        </Box>
        <Text {...styles.outOfStockText}>{outOfStockLabel}</Text>
      </Flex>
    );
  }

  return null;
};

ProductStockIndicator.displayName = 'ProductStockIndicator';

ProductStockIndicator.propTypes = {
  indicatorVariant: PropTypes.oneOf(['default', 'strip']),
  product: PropTypes.object,
  variant: PropTypes.object,
  onNotifyMeClick: PropTypes.func,
  shopperInventoryIds: PropTypes.string,
};

export default ProductStockIndicator;
